import type {
  ConnectionPoint,
  Point,
  ShapeStyle,
  ShapeType,
  Size,
} from '../state/types';
import { buildEdgeMidpoints } from './connectionPoints';
import { Shape } from './Shape';

export class Rectangle extends Shape {
  readonly type: ShapeType = 'rectangle';

  constructor(
    position: Point,
    size: Size,
    style?: Partial<ShapeStyle>,
    text = ''
  ) {
    super(position, size, style, text);
  }

  containsPoint(p: Point): boolean {
    return (
      p.x >= this.position.x &&
      p.x <= this.position.x + this.size.width &&
      p.y >= this.position.y &&
      p.y <= this.position.y + this.size.height
    );
  }

  getConnectionPoints(): ConnectionPoint[] {
    const { x, y } = this.position;
    const { width, height } = this.size;
    const edgeMidpoints = buildEdgeMidpoints(this.id, this.position, this.size);

    return [
      ...edgeMidpoints,
      // Corners
      {
        id: `${this.id}-top-left`,
        position: { x, y },
        direction: 'top',
      },
      {
        id: `${this.id}-top-right`,
        position: { x: x + width, y },
        direction: 'top',
      },
      {
        id: `${this.id}-bottom-right`,
        position: { x: x + width, y: y + height },
        direction: 'bottom',
      },
      {
        id: `${this.id}-bottom-left`,
        position: { x, y: y + height },
        direction: 'bottom',
      },
    ];
  }

  render(ctx: CanvasRenderingContext2D): void {
    const { x, y } = this.position;
    const { width, height } = this.size;
    const radius = Math.min(
      this.style.cornerRadius,
      width / 2,
      height / 2
    );

    ctx.save();
    ctx.globalAlpha = this.style.opacity;

    // Draw rounded rectangle
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();

    // Fill
    ctx.fillStyle = this.style.fill;
    ctx.fill();

    // Stroke
    ctx.strokeStyle = this.style.stroke;
    ctx.lineWidth = this.style.strokeWidth;
    ctx.stroke();

    ctx.restore();

    this.renderText(ctx);
    this.renderSelection(ctx);
  }
}
